// =============================================================================
// Offline cache + reconnect helpers.
//
// The app's source of truth is Supabase, but writes go through `syncData`
// which can fail when the user is offline. To support an "add stuff while
// offline → it shows up immediately → it syncs later" experience, we
// persist the in-memory data tree to localStorage on every write together
// with the last server-confirmed snapshot.
//
// On startup useData first checks the cache: if a fresh load from Supabase
// fails (offline), we boot from cache so the app is usable. When the
// browser fires `online` again, useData replays the diff from the cached
// `lastSynced` to the latest local state via the regular `syncData`
// pipeline, so no special replay code path is needed — the diff-based sync
// handles it.
//
// Keys are scoped per user-id so multiple accounts on one device don't
// collide.
// =============================================================================

const KEY_PREFIX = 'eulea_offline_v1:';

const dataKey = (userId) => `${KEY_PREFIX}${userId}:data`;
const syncedKey = (userId) => `${KEY_PREFIX}${userId}:lastSynced`;

const safeRead = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const safeWrite = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // QuotaExceeded etc. — safe to ignore, cache is best-effort.
  }
};

export const readCachedData = (userId) =>
  userId ? safeRead(dataKey(userId)) : null;

export const readCachedSynced = (userId) =>
  userId ? safeRead(syncedKey(userId)) : null;

export const writeCachedData = (userId, data) => {
  if (!userId) return;
  safeWrite(dataKey(userId), data);
};

export const writeCachedSynced = (userId, data) => {
  if (!userId) return;
  safeWrite(syncedKey(userId), data);
};

export const clearOfflineCache = (userId) => {
  if (!userId) return;
  try {
    window.localStorage.removeItem(dataKey(userId));
    window.localStorage.removeItem(syncedKey(userId));
  } catch {
    // ignore
  }
};

export const isOffline = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

// Heuristic for "this looks like the network is unreachable" so we can flip
// into offline-tolerant behaviour even if navigator.onLine still claims
// true (which Safari is fond of).
export const looksLikeNetworkError = (err) => {
  if (!err) return false;
  const msg = (err.message || '').toLowerCase();
  return (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('network request failed') ||
    msg.includes('load failed') ||
    err.name === 'TypeError' // browser fetch network error
  );
};
